import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, MARKET_META } from "../lib/api";
import { AdminShell } from "./AdminShell";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const todayISTString = () => new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const AdminResultForm = () => {
    const { marketKey } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const meta = MARKET_META[marketKey];

    const [date, setDate] = useState(searchParams.get("date") || todayISTString());
    const [fr, setFr] = useState("");
    const [sr, setSr] = useState("");
    const [resultId, setResultId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async (d) => {
        setLoading(true);
        try {
            const { data } = await api.get("/results", { params: { market_key: marketKey, date: d, limit: 1 } });
            if (data && data[0]) {
                setFr(data[0].fr ?? "");
                setSr(data[0].sr ?? "");
                setResultId(data[0].id);
            } else {
                setFr(""); setSr(""); setResultId(null);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(date); }, [date, marketKey]);
    const hydrated = !loading;

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/results", {
                market_key: marketKey,
                date,
                fr: fr === "" ? null : fr,
                sr: sr === "" ? null : sr,
            });
            toast.success("Result saved successfully");
            navigate(`/admin/previous/${marketKey}`);
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!meta) return <AdminShell title="Result"><div>Market not found</div></AdminShell>;

    return (
        <AdminShell title="Result">
            <form onSubmit={onSubmit} className={`max-w-xl ${!hydrated ? "opacity-60 pointer-events-none" : ""}`} data-testid="admin-result-form">
                <div
                    className="rounded-md px-4 py-2 mb-5 text-white text-sm font-semibold flex items-center gap-2"
                    style={{ background: meta.accent, color: meta.headerTextColor || "#FFF" }}
                >
                    <span className="text-lg">{meta.emoji}</span>
                    {meta.name}
                </div>

                <label className="block text-slate-500 font-medium mb-2">Date:</label>
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="admin-result-date"
                    className="w-full border border-slate-300 rounded-md px-3 py-3 text-lg font-num focus:border-blue-800 outline-none"
                />

                <label className="block text-slate-500 font-medium mb-2 mt-6">F/R ({meta.fr_time}):</label>
                <input
                    type="text"
                    inputMode="numeric"
                    value={fr}
                    onChange={(e) => setFr(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                    placeholder="XX"
                    data-testid="admin-result-fr"
                    className="w-full border border-slate-300 rounded-md px-3 py-3 text-2xl result-digit focus:border-blue-800 outline-none"
                />

                <label className="block text-slate-500 font-medium mb-2 mt-6">S/R ({meta.sr_time}):</label>
                <input
                    type="text"
                    inputMode="numeric"
                    value={sr}
                    onChange={(e) => setSr(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                    placeholder="XX"
                    data-testid="admin-result-sr"
                    className="w-full border border-slate-300 rounded-md px-3 py-3 text-2xl result-digit focus:border-blue-800 outline-none"
                />

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="submit"
                        disabled={saving || loading}
                        data-testid="admin-result-submit"
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-60"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Submit
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/admin")}
                        data-testid="admin-result-back"
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-slate-500 text-white text-sm font-semibold hover:bg-slate-600"
                    >
                        Back
                    </button>
                </div>
            </form>
        </AdminShell>
    );
};

export default AdminResultForm;
